import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, ArrowRight, Sparkles, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ActivitiesStep({ sessionNumber, onDone }) {
  const [pleasant, setPleasant] = useState(["", "", ""]);
  const [mastery, setMastery] = useState(["", "", ""]);
  const [saving, setSaving] = useState(false);

  const updateList = (setter, list, i, v) => setter(list.map((x, idx) => (idx === i ? v : x)));
  const removeItem = (setter, list, i) => setter(list.filter((_, idx) => idx !== i));
  const addItem = (setter, list) => setter([...list, ""]);

  const save = async () => {
    const filtered = {
      pleasant: pleasant.filter(x => x.trim()),
      mastery: mastery.filter(x => x.trim()),
    };
    if (filtered.pleasant.length < 1 || filtered.mastery.length < 1) {
      toast.error("Añade al menos una actividad en cada categoría.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/progress", { session_number: sessionNumber, step: "activities", completed: true, payload: filtered });
      toast.success("Actividades guardadas");
      onDone(filtered);
    } catch {
      toast.error("No pudimos guardar las actividades.");
    } finally {
      setSaving(false);
    }
  };

  const List = ({ title, icon: Icon, list, setter, testPrefix, color }) => (
    <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.75} />
          <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">{title}</p>
        </div>
        <div className="space-y-2">
          {list.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                data-testid={`${testPrefix}-${i}`}
                value={v}
                onChange={(e) => updateList(setter, list, i, e.target.value)}
                placeholder={`Actividad ${i + 1}`}
                className="rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)]"
              />
              {list.length > 1 && (
                <button onClick={() => removeItem(setter, list, i)} className="h-9 w-9 rounded-full grid place-items-center border border-[var(--ha-border)] hover:bg-[var(--ha-bg)]" aria-label="Eliminar">
                  <X className="w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => addItem(setter, list)} data-testid={`${testPrefix}-add`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-[var(--ha-primary)] hover:underline">
            <Plus className="w-4 h-4" strokeWidth={1.75} /> Añadir otra
          </button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">Piensa en actividades de tu día a día. Las <strong>placenteras</strong> te generan disfrute; las <strong>de dominio</strong> te dan sensación de logro.</p>
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <List title="Placenteras" icon={Sparkles} list={pleasant} setter={setPleasant} testPrefix="pleasant" color="var(--ha-secondary)" />
        <List title="De dominio" icon={Trophy} list={mastery} setter={setMastery} testPrefix="mastery" color="var(--ha-primary)" />
      </div>
      <div className="mt-8 flex justify-end">
        <Button data-testid="activities-continue-btn" onClick={save} disabled={saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Guardar y continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
